<template>
  <div class="transaction-list-container">
    <div class="card">
      <h2>交易记录</h2>
      <p class="page-lead">
        每一笔都会标明是<strong>增加</strong>还是<strong>扣除</strong>，并显示完成后的余额。金额单位为元。
      </p>
      <div class="record-actions">
        <el-button type="primary" size="small" @click="$router.push('/recharge')">充值</el-button>
        <el-button type="warning" size="small" @click="$router.push('/withdraw/apply')">余额提现</el-button>
        <el-button size="small" @click="$router.push('/real-name-verification')">实名认证</el-button>
        <el-button size="small" @click="$router.push('/my')">我的</el-button>
      </div>

      <div class="legend">
        <span class="legend-item text-success">增加：余额变多</span>
        <span class="legend-item text-danger">扣除：余额变少</span>
        <span class="legend-item">余额：本笔完成后的账户余额</span>
      </div>

      <div class="month-section">
        <div class="month-section__head">
          <h3>按月汇总</h3>
          <el-button v-if="filters.month" size="small" text type="primary" @click="selectMonth('')">查看全部月份</el-button>
        </div>
        <div v-if="monthSummaries.length" class="month-strip">
          <button
            v-for="m in monthSummaries"
            :key="m.month"
            type="button"
            class="month-card"
            :class="{ 'month-card--active': filters.month === m.month }"
            @click="selectMonth(m.month)"
          >
            <div class="month-card__title">{{ m.month }}</div>
            <div class="month-card__inc">增加 ¥{{ formatAmount(m.income) }}</div>
            <div class="month-card__dec">扣除 ¥{{ formatAmount(m.expense) }}</div>
            <div class="month-card__net" :class="m.net >= 0 ? 'text-success' : 'text-danger'">
              净额 {{ formatSignedYuan(m.net) }}
            </div>
            <div class="month-card__count">{{ m.count }} 笔</div>
          </button>
        </div>
        <p v-else-if="!loading" class="month-empty">暂无按月数据</p>
      </div>

      <div class="filters mb-4">
        <div class="filter-row">
          <el-select v-model="filters.direction" placeholder="增加或扣除" class="filter-item" @change="onFilterChange">
            <el-option label="全部方向" :value="0" />
            <el-option label="仅增加" :value="1" />
            <el-option label="仅扣除" :value="2" />
          </el-select>
          <el-select v-model="filters.transType" placeholder="交易大类" class="filter-item" @change="onFilterChange">
            <el-option label="全部类型" :value="0" />
            <el-option label="充值 / 入账增加" :value="1" />
            <el-option label="消费 / 提现扣除" :value="2" />
            <el-option label="赠送 / 调账" :value="3" />
          </el-select>
          <el-select v-model="filters.month" placeholder="月份" class="filter-item" clearable @change="onFilterChange">
            <el-option label="全部月份" value="" />
            <el-option
              v-for="m in monthSummaries"
              :key="'opt-' + m.month"
              :label="m.month"
              :value="m.month"
            />
          </el-select>
          <el-input v-model="filters.orderNo" placeholder="订单号" class="filter-item" clearable @keyup.enter="onFilterChange" />
          <el-button type="primary" class="filter-item" @click="onFilterChange">
            <el-icon><Search /></el-icon>
            查找
          </el-button>
        </div>
      </div>

      <p v-if="refundConfig.complaintPhone" class="complaint-hint">
        消费订单可在 {{ refundConfig.appealDays || 3 }} 天内申诉退款；投诉热线：
        <a :href="'tel:' + refundConfig.complaintPhone">{{ refundConfig.complaintPhone }}</a>
      </p>

      <el-table
        :data="transactions"
        style="width: 100%"
        border
        v-loading="loading"
      >
        <el-table-column
          prop="createdAt"
          label="时间"
          width="170"
        >
          <template #default="{ row }">
            {{ formatDate(row.createdAt) }}
          </template>
        </el-table-column>
        <el-table-column label="方向" width="88">
          <template #default="{ row }">
            <el-tag :type="row.direction === 'decrease' ? 'danger' : 'success'" size="small">
              {{ row.directionLabel || (row.direction === 'decrease' ? '扣除' : '增加') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="类型" min-width="140">
          <template #default="{ row }">
            <div class="type-cell">
              <el-tag :type="typeTagTone(row)" size="small" effect="plain">{{ row.typeLabel || '其他' }}</el-tag>
              <span class="type-cell__pay">{{ row.payType || '—' }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="说明" min-width="200">
          <template #default="{ row }">
            <div class="detail-cell">{{ row.detail || '—' }}</div>
            <div v-if="row.orderNo" class="order-cell">订单 {{ row.orderNo }}</div>
          </template>
        </el-table-column>
        <el-table-column label="变动金额" width="120">
          <template #default="{ row }">
            <span :class="amountClass(row)">{{ formatSignedYuan(row.signedAmount) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="完成后余额" width="120">
          <template #default="{ row }">
            ¥{{ formatAmount(row.balance) }}
          </template>
        </el-table-column>
        <el-table-column label="申诉" width="140" fixed="right">
          <template #default="{ row }">
            <template v-if="row.transType === 2 && (row.canAppeal || row.appealStatus)">
              <el-button
                v-if="row.canAppeal"
                link
                type="warning"
                @click="openAppealDialog(row)"
              >
                申诉退款
              </el-button>
              <el-button
                v-else-if="row.appealStatus === 'need_info' && isWithinAppealDeadline(row)"
                link
                type="primary"
                @click="openSupplementDialog(row)"
              >
                补充材料
              </el-button>
              <el-tag v-else-if="row.appealStatus" size="small" :type="appealTagType(row.appealStatus)">
                {{ appealStatusLabel(row.appealStatus) }}
              </el-tag>
            </template>
            <span v-else>—</span>
          </template>
        </el-table-column>
      </el-table>

      <el-dialog v-model="appealVisible" title="申诉退款" width="520px" destroy-on-close>
        <p v-if="appealOrderNo" class="dialog-order">订单号：{{ appealOrderNo }}</p>
        <el-form label-width="80px">
          <el-form-item label="申诉理由" required>
            <el-input v-model="appealForm.reason" type="textarea" :rows="4" maxlength="500" show-word-limit />
          </el-form-item>
          <el-form-item label="凭证图片">
            <el-upload
              action="/api/v1/upload"
              list-type="picture-card"
              :headers="uploadHeaders"
              :file-list="appealFileList"
              :on-success="onAppealUploadSuccess"
              :on-remove="onAppealUploadRemove"
              :before-upload="beforeAppealUpload"
              accept="image/*"
            >
              <el-icon><Plus /></el-icon>
            </el-upload>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="appealVisible = false">取消</el-button>
          <el-button type="primary" :loading="appealSubmitting" @click="submitAppeal">提交申诉</el-button>
        </template>
      </el-dialog>

      <el-dialog v-model="supplementVisible" title="补充申诉材料" width="520px" destroy-on-close>
        <p v-if="supplementOrderNo" class="dialog-order">订单号：{{ supplementOrderNo }}</p>
        <el-alert v-if="supplementAdminReply" type="info" :closable="false" class="mb-3">
          管理员要求：{{ supplementAdminReply }}
        </el-alert>
        <el-form label-width="80px">
          <el-form-item label="补充说明">
            <el-input v-model="supplementForm.reason" type="textarea" :rows="3" maxlength="500" show-word-limit />
          </el-form-item>
          <el-form-item label="凭证图片">
            <el-upload
              action="/api/v1/upload"
              list-type="picture-card"
              :headers="uploadHeaders"
              :file-list="supplementFileList"
              :on-success="onSupplementUploadSuccess"
              :on-remove="onSupplementUploadRemove"
              :before-upload="beforeAppealUpload"
              accept="image/*"
            >
              <el-icon><Plus /></el-icon>
            </el-upload>
          </el-form-item>
        </el-form>
        <template #footer>
          <el-button @click="supplementVisible = false">取消</el-button>
          <el-button type="primary" :loading="supplementSubmitting" @click="submitSupplement">提交</el-button>
        </template>
      </el-dialog>

      <AdminPagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :total="total"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>
  </div>
</template>

<script>
import { goToLogin } from '@/utils/loginFlow'
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import { Search, Plus } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { useRouter } from 'vue-router'
import AdminPagination from '@/components/AdminPagination.vue'
import { adminUploadHeaders, uploadResponseToUrl, validatePhotoUpload } from '@/utils/photoUpload'
import {
  appealStatusLabel,
  fetchPurchaseRefundAppeal,
  fetchPurchaseRefundConfig,
  submitPurchaseRefundAppeal,
  supplementPurchaseRefundAppeal
} from '@/utils/purchaseRefund'

export default {
  name: 'TransactionListView',
  components: {
    Search,
    Plus,
    AdminPagination
  },
  setup() {
    const transactions = ref([])
    const monthSummaries = ref([])
    const total = ref(0)
    const currentPage = ref(1)
    const pageSize = ref(20)
    const loading = ref(false)
    const refundConfig = ref({ appealDays: 3, complaintPhone: '' })
    const filters = ref({
      transType: 0,
      direction: 0,
      month: '',
      orderNo: ''
    })

    const appealVisible = ref(false)
    const appealSubmitting = ref(false)
    const appealOrderNo = ref('')
    const appealForm = ref({ reason: '', imageUrls: [] })
    const appealFileList = ref([])

    const supplementVisible = ref(false)
    const supplementSubmitting = ref(false)
    const supplementOrderNo = ref('')
    const supplementAppealId = ref(0)
    const supplementAdminReply = ref('')
    const supplementForm = ref({ reason: '', imageUrls: [] })
    const supplementFileList = ref([])

    const router = useRouter()
    const uploadHeaders = computed(() => adminUploadHeaders())

    const checkLogin = () => {
      if (!localStorage.getItem('jwt_token')) {
        goToLogin(router)
        return false
      }
      return true
    }

    const loadRefundConfig = async () => {
      try {
        refundConfig.value = await fetchPurchaseRefundConfig()
      } catch {
        refundConfig.value = { appealDays: 3, complaintPhone: '400-888-0000' }
      }
    }

    const loadTransactions = async () => {
      try {
        loading.value = true
        const token = localStorage.getItem('jwt_token')
        const response = await axios.post('/api/v1/transactions/list', {
          page: currentPage.value,
          pageSize: pageSize.value,
          transType: filters.value.transType || 0,
          direction: filters.value.direction || 0,
          month: filters.value.month || '',
          orderNo: filters.value.orderNo || ''
        }, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        })

        if (response.data.code === 200) {
          transactions.value = response.data.data.trans || []
          total.value = response.data.data.total || 0
          monthSummaries.value = response.data.data.monthSummaries || []
        } else {
          ElMessage.error(response.data.message || '获取交易记录失败')
        }
      } catch (error) {
        console.error('Failed to load transactions:', error)
        ElMessage.error('获取交易记录失败')
      } finally {
        loading.value = false
      }
    }

    const onFilterChange = () => {
      currentPage.value = 1
      loadTransactions()
    }

    const selectMonth = (month) => {
      filters.value.month = month || ''
      onFilterChange()
    }

    const isWithinAppealDeadline = (row) => {
      if (!row?.appealDeadlineAt) return false
      return row.appealDeadlineAt * 1000 >= Date.now()
    }

    const appealTagType = (status) => {
      const map = { pending: 'warning', need_info: 'info', approved: 'success', rejected: 'danger' }
      return map[status] || 'info'
    }

    const resetAppealForm = () => {
      appealForm.value = { reason: '', imageUrls: [] }
      appealFileList.value = []
    }

    const openAppealDialog = (row) => {
      appealOrderNo.value = row.orderNo
      resetAppealForm()
      appealVisible.value = true
    }

    const beforeAppealUpload = (file) => validatePhotoUpload(file, ElMessage.error)

    const onAppealUploadSuccess = (response, file, fileList) => {
      const url = uploadResponseToUrl(response)
      if (url) {
        appealForm.value.imageUrls.push(url)
      }
      appealFileList.value = fileList
    }

    const onAppealUploadRemove = (file, fileList) => {
      const url = file.response ? uploadResponseToUrl(file.response) : file.url
      appealForm.value.imageUrls = appealForm.value.imageUrls.filter(u => u !== url)
      appealFileList.value = fileList
    }

    const submitAppeal = async () => {
      const reason = appealForm.value.reason.trim()
      if (!reason) {
        ElMessage.warning('请填写申诉理由')
        return
      }
      appealSubmitting.value = true
      try {
        await submitPurchaseRefundAppeal({
          orderNo: appealOrderNo.value,
          reason,
          imageUrls: appealForm.value.imageUrls
        })
        ElMessage.success('申诉已提交，请等待审核')
        appealVisible.value = false
        loadTransactions()
      } catch (e) {
        ElMessage.error(e.message || '提交失败')
      } finally {
        appealSubmitting.value = false
      }
    }

    const openSupplementDialog = async (row) => {
      supplementOrderNo.value = row.orderNo
      supplementForm.value = { reason: '', imageUrls: [] }
      supplementFileList.value = []
      supplementAdminReply.value = ''
      supplementAppealId.value = 0
      try {
        const data = await fetchPurchaseRefundAppeal(row.orderNo)
        supplementAppealId.value = data?.id || 0
        supplementAdminReply.value = data?.adminReply || ''
      } catch (e) {
        ElMessage.error(e.message || '加载申诉信息失败')
        return
      }
      supplementVisible.value = true
    }

    const onSupplementUploadSuccess = (response, file, fileList) => {
      const url = uploadResponseToUrl(response)
      if (url) {
        supplementForm.value.imageUrls.push(url)
      }
      supplementFileList.value = fileList
    }

    const onSupplementUploadRemove = (file, fileList) => {
      const url = file.response ? uploadResponseToUrl(file.response) : file.url
      supplementForm.value.imageUrls = supplementForm.value.imageUrls.filter(u => u !== url)
      supplementFileList.value = fileList
    }

    const submitSupplement = async () => {
      if (!supplementAppealId.value) {
        ElMessage.error('申诉记录无效')
        return
      }
      supplementSubmitting.value = true
      try {
        await supplementPurchaseRefundAppeal({
          appealId: supplementAppealId.value,
          reason: supplementForm.value.reason.trim(),
          imageUrls: supplementForm.value.imageUrls
        })
        ElMessage.success('材料已提交')
        supplementVisible.value = false
        loadTransactions()
      } catch (e) {
        ElMessage.error(e.message || '提交失败')
      } finally {
        supplementSubmitting.value = false
      }
    }

    const handlePageChange = (page) => {
      currentPage.value = page
      loadTransactions()
    }

    const handleSizeChange = (size) => {
      pageSize.value = size
      currentPage.value = 1
      loadTransactions()
    }

    const formatDate = (timestamp) => {
      if (!timestamp) return '—'
      return new Date(timestamp * 1000).toLocaleString()
    }

    const formatAmount = (amount) => {
      if (!amount && amount !== 0) return '0.00'
      return (Number(amount) / 100).toFixed(2)
    }

    const formatSignedYuan = (cents) => {
      const n = Number(cents || 0) / 100
      const abs = Math.abs(n).toFixed(2)
      if (n > 0) return `+¥${abs}`
      if (n < 0) return `-¥${abs}`
      return `¥${abs}`
    }

    const amountClass = (row) => {
      if (row.direction === 'decrease' || Number(row.signedAmount) < 0) return 'text-danger'
      return 'text-success'
    }

    const typeTagTone = (row) => {
      if (row.direction === 'decrease') return 'danger'
      if (row.typeLabel === '赠送') return 'warning'
      if (row.typeLabel === '收入') return 'success'
      return 'success'
    }

    onMounted(() => {
      if (!checkLogin()) {
        return
      }
      loadRefundConfig()
      loadTransactions()
    })

    return {
      transactions,
      monthSummaries,
      total,
      currentPage,
      pageSize,
      loading,
      filters,
      refundConfig,
      appealVisible,
      appealSubmitting,
      appealOrderNo,
      appealForm,
      appealFileList,
      supplementVisible,
      supplementSubmitting,
      supplementOrderNo,
      supplementAdminReply,
      supplementForm,
      supplementFileList,
      uploadHeaders,
      loadTransactions,
      onFilterChange,
      selectMonth,
      handlePageChange,
      handleSizeChange,
      formatDate,
      formatAmount,
      formatSignedYuan,
      amountClass,
      typeTagTone,
      appealStatusLabel,
      appealTagType,
      isWithinAppealDeadline,
      openAppealDialog,
      openSupplementDialog,
      submitAppeal,
      submitSupplement,
      beforeAppealUpload,
      onAppealUploadSuccess,
      onAppealUploadRemove,
      onSupplementUploadSuccess,
      onSupplementUploadRemove
    }
  }
}
</script>

<style scoped>
.transaction-list-container {
  padding: 20px;
  max-width: 880px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}

.card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
}

.page-lead {
  margin: 8px 0 0;
  font-size: 14px;
  color: var(--el-text-color-secondary);
  line-height: 1.6;
}

.record-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 12px 0 16px;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 20px;
  margin-bottom: 16px;
  font-size: 13px;
  color: var(--el-text-color-regular);
}

.legend-item {
  font-weight: 600;
}

.month-section {
  margin-bottom: 16px;
}

.month-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.month-section__head h3 {
  margin: 0;
  font-size: 15px;
}

.month-strip {
  display: flex;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
}

.month-card {
  flex: 0 0 auto;
  min-width: 148px;
  text-align: left;
  padding: 10px 12px;
  border: 1px solid var(--el-border-color);
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
}

.month-card:hover {
  border-color: var(--el-color-primary);
}

.month-card--active {
  border-color: var(--el-color-primary);
  background: var(--el-color-primary-light-9);
}

.month-card__title {
  font-weight: 700;
  margin-bottom: 4px;
}

.month-card__inc {
  color: var(--el-color-success);
  font-size: 13px;
}

.month-card__dec {
  color: var(--el-color-danger);
  font-size: 13px;
}

.month-card__net {
  font-size: 13px;
  font-weight: 600;
  margin-top: 2px;
}

.month-card__count {
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.month-empty {
  margin: 0;
  font-size: 13px;
  color: var(--el-text-color-secondary);
}

.filters {
  margin-bottom: 20px;
}

.complaint-hint {
  margin: 0 0 16px;
  font-size: 14px;
  color: var(--el-text-color-secondary);
}

.complaint-hint a {
  color: var(--el-color-primary);
  text-decoration: none;
}

.dialog-order {
  margin: 0 0 12px;
  font-size: 14px;
  color: var(--el-text-color-regular);
}

.mb-3 {
  margin-bottom: 12px;
}

.type-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: flex-start;
}

.type-cell__pay {
  font-size: 13px;
  color: var(--el-text-color-primary);
}

.detail-cell {
  line-height: 1.5;
}

.order-cell {
  margin-top: 4px;
  font-size: 12px;
  color: var(--el-text-color-secondary);
}

.text-success {
  color: var(--el-color-success);
}

.text-danger {
  color: var(--el-color-danger);
}

:deep(.el-table) {
  --el-table-border-color: var(--el-border-color-lighter);
  --el-table-header-bg-color: var(--el-fill-color-light);
}

:deep(.el-table th) {
  font-weight: 600;
  color: var(--el-text-color-primary);
}

:deep(.el-table td) {
  padding: 12px 0;
}

:deep(.el-tag) {
  font-weight: 500;
}

@media (max-width: 768px) {
  .transaction-list-container {
    padding: 10px;
  }

  .card {
    padding: 15px;
  }

  .filter-row {
    flex-wrap: wrap;
  }

  .filter-item {
    max-width: none;
    flex: 1 1 140px;
  }
}

.filter-row {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.filter-item {
  flex: 1;
  max-width: 200px;
}

.filter-item:last-child {
  flex: 0 0 auto;
  max-width: none;
}
</style>
